<template>
  <div class="spacing">
    <div class="spacing__ring">
      <span class="spacing__tag">{{ $t("custom.spacing_input.margin") }}</span>
      <button
        type="button"
        class="spacing__link"
        :title="marginLinked ? $t('custom.spacing_input.margin_separate') : $t('custom.spacing_input.margin_shared')"
        @click="toggleLink('margin', 'marginLinked')">
        <v-icon x-small>{{ marginLinked ? "mdi-link-variant" : "mdi-link-variant-off" }}</v-icon>
      </button>
      <input
        v-for="side in SIDES"
        :key="`margin-${side.key}`"
        class="spacing__value"
        :class="[`spacing__value--${side.key}`, { 'spacing__value--linked': marginLinked }]"
        type="number"
        min="0"
        :value="margin[side.key]"
        :disabled="isSideMarginDisabled(side.key)"
        :title="
          isSideMarginDisabled(side.key)
            ? $t('custom.spacing_input.full_width_margin')
            : $t('custom.spacing_input.side_margin', { side: $t(`custom.sides.${side.key}`) })
        "
        @input="setSide('margin', side.key, $event.target.value, marginLinked)" />

      <div class="spacing__ring spacing__ring--inner">
        <span class="spacing__tag">{{ $t("custom.spacing_input.padding") }}</span>
        <button
          type="button"
          class="spacing__link"
          :title="
            paddingLinked ? $t('custom.spacing_input.padding_separate') : $t('custom.spacing_input.padding_shared')
          "
          @click="toggleLink('padding', 'paddingLinked')">
          <v-icon x-small>{{ paddingLinked ? "mdi-link-variant" : "mdi-link-variant-off" }}</v-icon>
        </button>
        <input
          v-for="side in SIDES"
          :key="`padding-${side.key}`"
          class="spacing__value"
          :class="[`spacing__value--${side.key}`, { 'spacing__value--linked': paddingLinked }]"
          type="number"
          min="0"
          :value="padding[side.key]"
          :title="$t('custom.spacing_input.side_padding', { side: $t(`custom.sides.${side.key}`) })"
          @input="setSide('padding', side.key, $event.target.value, paddingLinked)" />

        <span class="spacing__content" />
      </div>
    </div>
  </div>
</template>

<script>
  const SIDES = [{ key: "top" }, { key: "right" }, { key: "bottom" }, { key: "left" }];

  const HORIZONTAL_SIDES = ["left", "right"];

  export default {
    name: "VaSpacingInput",
    props: {
      margin: { type: Object, required: true },
      padding: { type: Object, required: true },
      marginLinked: { type: Boolean, default: false },
      paddingLinked: { type: Boolean, default: false },
      sideMarginsDisabled: { type: Boolean, default: false },
    },
    data() {
      return { SIDES };
    },
    methods: {
      isSideMarginDisabled(key) {
        return this.sideMarginsDisabled && HORIZONTAL_SIDES.includes(key);
      },
      setSide(name, key, value, linked) {
        const length = Math.max(0, Number.parseFloat(value) || 0);
        const keys = linked ? SIDES.map(side => side.key) : [key];

        this.emitBox(name, keys, length);
      },
      toggleLink(name, flag) {
        const linked = !this[flag];

        this.$emit(`update:${flag}`, linked);

        if (linked) {
          this.emitBox(
            name,
            SIDES.map(side => side.key),
            this[name].top,
          );
        }
      },
      emitBox(name, keys, length) {
        const next = { ...this[name] };

        keys.forEach(side => {
          next[side] = length;
        });

        this.$emit(`update:${name}`, next);
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import "@core/preset/preset/functions.scss";
  @import "@core/preset/preset/typography/index.scss";

  $diagonal: get-color("element-bg-300");

  @mixin corner-diagonals {
    background-image: linear-gradient(
        to bottom right,
        transparent calc(50% - 0.5px),
        $diagonal calc(50% - 0.5px),
        $diagonal calc(50% + 0.5px),
        transparent calc(50% + 0.5px)
      ),
      linear-gradient(
        to bottom left,
        transparent calc(50% - 0.5px),
        $diagonal calc(50% - 0.5px),
        $diagonal calc(50% + 0.5px),
        transparent calc(50% + 0.5px)
      );
  }

  .spacing {
    &__ring {
      @include corner-diagonals;

      position: relative;
      padding: 30px 58px;
      border-radius: 4px;
      background-color: get-color("element-bg-200");

      &--inner {
        background-color: get-color("hover");
      }
    }

    &__tag {
      position: absolute;
      top: 5px;
      left: 8px;
      font-size: 10px;
      font-style: italic;
      font-weight: 600;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      color: get-color("subtitle");
    }

    &__link {
      position: absolute;
      top: 3px;
      right: 4px;
      padding: 0;
      border: 0;
      background: none;
      cursor: pointer;
      opacity: 0.45;

      &:hover {
        opacity: 1;
      }
    }

    &__content {
      display: block;
      height: 26px;
      border-radius: 3px;
      background-color: get-color("element-bg-300");
    }

    &__value {
      position: absolute;
      @include text-caption;

      width: 46px;
      padding: 1px 0;
      text-align: center;
      color: get-color("body");
      border: 0;
      border-radius: 3px;
      background-color: transparent;
      outline: none;
      appearance: textfield;

      &::-webkit-inner-spin-button,
      &::-webkit-outer-spin-button {
        appearance: none;
        margin: 0;
      }

      &:hover:not(:disabled) {
        background-color: get-color("element-bg-300");
      }

      &:focus {
        color: get-color("primary");
        background-color: get-color("white");
        box-shadow: 0 0 0 1px get-color("primary");
      }

      &:disabled {
        color: get-color("disabled");
        cursor: not-allowed;
      }

      &--linked {
        color: get-color("primary");
      }

      &--top,
      &--bottom {
        left: 50%;
        transform: translateX(-50%);
      }

      &--left,
      &--right {
        top: 50%;
        transform: translateY(-50%);
      }

      &--top {
        top: 6px;
      }

      &--bottom {
        bottom: 6px;
      }

      &--left {
        left: 6px;
      }

      &--right {
        right: 6px;
      }
    }
  }
</style>
